import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { LabeledField } from './LabeledField';
import { Note } from './Note';
import { DISPLAY, tokens } from '../theme';

/** Trùng với ràng buộc ở `packages/shared/src/auth.ts` — server vẫn là nơi kiểm thật. */
const MIN_PASSWORD = 10;

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const change = useMutation({
    mutationFn: () => api.changePassword({ currentPassword: current, newPassword: next }),
  });

  function close() {
    setCurrent('');
    setNext('');
    setConfirm('');
    change.reset();
    onClose();
  }

  // Kiểm tra "nhập lại khớp" ở phía giao diện, vì server không cần biết ô này —
  // gửi lên rồi mới báo lệch là bắt người dùng chờ một vòng vô ích.
  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD;
  const canSubmit =
    current.length > 0 && next.length >= MIN_PASSWORD && next === confirm && !change.isPending;

  return (
    <Dialog open={open} onClose={change.isPending ? undefined : close} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 600, color: tokens.ink }}>
        Đổi mật khẩu
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          spacing={2}
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) change.mutate();
          }}
        >
          <LabeledField
            label="Mật khẩu hiện tại"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <LabeledField
            label="Mật khẩu mới"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            error={tooShort}
            helperText={tooShort ? `Cần từ ${MIN_PASSWORD} ký tự` : `Tối thiểu ${MIN_PASSWORD} ký tự`}
          />
          <LabeledField
            label="Nhập lại mật khẩu mới"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={mismatch}
            helperText={mismatch ? 'Hai lần nhập không giống nhau' : undefined}
          />

          {change.isError && <Note tone="bad">{(change.error as Error).message}</Note>}
          {change.isSuccess && (
            <Note>Đã đổi mật khẩu. Các phiên đang mở ở máy khác đã bị đăng xuất.</Note>
          )}

          {/* Câu này trước đây nói ngược lại — phiên cũ sống tới 12 giờ. Spec SC-10
              §3.5 đã sửa hành vi đó: đổi mật khẩu thu hồi mọi phiên khác ngay. */}
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
            Đổi mật khẩu sẽ đăng xuất mọi phiên đang mở ở máy khác. Phiên hiện tại vẫn dùng tiếp
            được, không phải đăng nhập lại.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} color="inherit" disabled={change.isPending}>
          {change.isSuccess ? 'Đóng' : 'Huỷ'}
        </Button>
        <Button
          variant="contained"
          onClick={() => change.mutate()}
          disabled={!canSubmit || change.isSuccess}
          startIcon={change.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {change.isPending ? 'Đang đổi…' : 'Đổi mật khẩu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
